// MCP assistant connections, explicit proposal approval and local helpers.
import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { Body, Button, Caption, Choice, Field, ListItem, Row, Screen, Section } from '../components/ui';
import { confirm, notify } from '../components/Dialogs';
import { copyText } from '../platform/clipboard';
import * as ai from '../platform/ai';
import { speechSupported } from '../platform/speech';
import { ApiError } from '../platform/auth';
import { useAccount, withReauth } from '../store/account';
import { newId, useAllIngredients, useLibrary } from '../store/library';
import { useShopping } from '../store/shopping';
import { usePlanner } from '../store/planner';
import { draftToRecipe, resolver, toLine } from '../logic/aiDraft';
import { formatCountdown } from '../logic/format';
import { useNav } from '../navigation';
import { useToday } from '../hooks';
import { spacing, usePalette } from '../theme';
import type { AiDraftLine, AiInboxItem, AiRecipeDraft, AiStatus, AiToken } from '../types';

type Created = AiToken & { token: string; url: string };

export default function AiScreen() {
  const { t } = useTranslation();
  const p = usePalette();
  const nav = useNav();
  const today = useToday();
  const user = useAccount(s => s.user);
  const known = useAllIngredients();
  const put = useLibrary(s => s.put);
  const units = useLibrary(s => s.units);
  const addLoose = useShopping(s => s.addLooseItem);
  const addToPantry = useShopping(s => s.addToPantry);
  const addEntries = usePlanner(s => s.addEntries);

  const [status, setStatus] = useState<AiStatus | null>(null);
  const [tokens, setTokens] = useState<AiToken[]>([]);
  const [inbox, setInbox] = useState<AiInboxItem[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<{ code: string; wait?: number } | null>(null);
  const [label, setLabel] = useState('Claude');
  const [scope, setScope] = useState<AiToken['scope']>('propose');
  const [created, setCreated] = useState<Created | null>(null);

  const online = ai.aiAvailable && !!user;
  const refresh = useCallback(async () => {
    if (!online) return;
    try {
      const [s, tk, ib] = await Promise.all([ai.status(), ai.tokens(), ai.inbox()]);
      setStatus(s);
      setTokens(tk);
      setInbox(ib);
    } catch (e) {
      setError({ code: e instanceof ApiError ? e.code : 'generic' });
    }
  }, [online]);
  useFocusEffect(useCallback(() => {
    refresh();
  }, [refresh]));

  const run = async (key: string, work: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await work();
    } catch (e) {
      setError({ code: e instanceof ApiError ? e.code : 'generic', wait: e instanceof ApiError ? e.retryAfter : undefined });
    } finally {
      setBusy(null);
      ai.status().then(setStatus, () => {});
    }
  };

  const saveRecipe = (draft: AiRecipeDraft) => {
    const { recipe, created: newIngredients } = draftToRecipe(draft, known, newId, Date.now());
    for (const ing of newIngredients) put('ingredients', ing);
    put('recipes', recipe);
    // Through the Search tab (the library lives there): when this page was opened by its link (/ai), no tab stack is mounted yet.
    nav.navigate('Tabs', { screen: 'SearchTab', params: { screen: 'Recipe', params: { id: recipe.id, edit: true } } } as never);
  };
  const linesToItems = (lines: AiDraftLine[]) => {
    const r = resolver(known, newId);
    const items = lines.map(l => toLine(l, r.resolve));
    for (const ing of r.created) put('ingredients', ing);
    return items;
  };
  const acceptPantry = (lines: AiDraftLine[]) => {
    addToPantry(linesToItems(lines).map(l => ({ refId: l.ingredientId, amount: l.amount, unit: l.unit })), units, today);
  };
  const accept = (item: AiInboxItem) => run(`accept:${item.id}`, async () => {
    if (item.kind === 'recipe') saveRecipe(item.payload);
    else if (item.kind === 'grocery') for (const l of linesToItems(item.payload.items)) addLoose('ingredient', l.ingredientId, l.amount, l.unit);
    else if (item.kind === 'pantry') acceptPantry(item.payload.items);
    else if (item.kind === 'plan') addEntries(item.payload.entries.map(e => ({ id: newId(), date: e.date, slot: e.slot, recipeId: e.recipeId })));
    await ai.removeInboxItem(item.id);
    setInbox(list => (list ?? []).filter(x => x.id !== item.id));
    if (item.kind !== 'recipe') notify(t('ai.accepted'));
  });
  const dismiss = (item: AiInboxItem) => run(`dismiss:${item.id}`, async () => {
    await ai.removeInboxItem(item.id);
    setInbox(list => (list ?? []).filter(x => x.id !== item.id));
  });

  const connect = () => run('token', async () => {
    const made = await withReauth(() => ai.createToken(label.trim() || 'Assistant', scope));
    setCreated(made);
    setTokens(await ai.tokens());
  });
  const revoke = async (tk: AiToken) => {
    if (!(await confirm(t('ai.disconnectTitle', { name: tk.label }), t('ai.disconnectBody'), t('ai.disconnect')))) return;
    run(`revoke:${tk.id}`, async () => setTokens(await ai.revokeToken(tk.id)));
  };
  const setup = created ? `claude mcp add --transport http chefi ${created.url} --header "Authorization: Bearer ${created.token}"` : '';

  const kindLabel = (item: AiInboxItem) => t(`ai.kinds.${item.kind}`);
  const summary = (item: AiInboxItem) =>
    item.kind === 'recipe' ? item.payload.title
      : item.kind === 'plan' ? item.payload.entries.map(e => `${e.date} ${t(`planner.${e.slot}`)}: ${e.title}`).slice(0, 4).join('\n')
        : item.payload.items.map(l => [l.amount, l.unit, l.name].filter(x => x !== null && x !== '').join(' ')).slice(0, 6).join(', ');

  return (
    <Screen>
      <Section title={t('ai.deviceTitle')} icon="magic">
        <Caption>{t('ai.deviceBody')}</Caption>
        <ListItem title={t('ai.deviceIngredients')} subtitle={t('ai.deviceIngredientsNote')} icon="ingredient" />
        <ListItem title={t('ai.deviceVoice')} subtitle={speechSupported ? t('ai.deviceVoiceNote') : t('ai.deviceVoiceMissing')} icon="mic" />
      </Section>

      {error ? <Caption color={p.danger}>{t(`ai.errors.${error.code}`, { defaultValue: t('ai.errors.generic'), time: formatCountdown(error.wait ?? 0) })}</Caption> : null}
      {!online ? <Button label={t('ai.signIn')} icon="lock" variant="secondary" onPress={() => nav.navigate('Account')} /> : null}

      {online ? (
        <Section title={t('ai.assistantsTitle')} icon="assistant">
          <Caption>{t('ai.assistantsBody')}</Caption>
          {status ? (
            <View style={{ gap: spacing.s }}>
              <Body>{t('ai.connectorHow')}</Body>
              <Body>{status.mcpUrl}</Body>
              <Row>
                <Button label={t('ai.copyAddress')} icon="copy" variant="secondary" onPress={() => { copyText(status.mcpUrl); notify(t('ai.copied')); }} />
              </Row>
            </View>
          ) : null}
          {tokens.map(tk => (
            <ListItem key={tk.id} title={tk.label} icon="assistant"
              subtitle={`${t(`ai.scopes.${tk.scope}`)}${tk.oauth ? ` · ${t('ai.viaOAuth')}` : ''} · ${tk.lastUsedAt ? t('ai.lastUsed', { date: new Date(tk.lastUsedAt).toLocaleDateString() }) : t('ai.neverUsed')}`}
              onPress={() => revoke(tk)} />
          ))}
          {created ? (
            <View style={{ gap: spacing.s }}>
              <Body>{t('ai.tokenOnce')}</Body>
              <Caption>{t('ai.tokenUrl')}</Caption>
              <Body>{created.url}</Body>
              <Caption>{t('ai.tokenHeader')}</Caption>
              <Body>{`Bearer ${created.token}`}</Body>
              <Row>
                <Button label={t('ai.copyToken')} icon="copy" variant="secondary" onPress={() => { copyText(created.token); notify(t('ai.copied')); }} />
                <Button label={t('ai.copyClaudeCode')} icon="copy" variant="secondary" onPress={() => { copyText(setup); notify(t('ai.copied')); }} />
                <Button label={t('common.done')} onPress={() => setCreated(null)} />
              </Row>
            </View>
          ) : (
            <>
              <Field label={t('ai.tokenLabel')} value={label} onChangeText={setLabel} maxLength={40} />
              <Choice options={['propose', 'full'] as const} value={scope} onChange={setScope} labels={v => t(`ai.scopes.${v}`)} />
              <Caption>{t(`ai.scopeNotes.${scope}`)}</Caption>
              <Caption>{t('ai.tokenHow')}</Caption>
              <Button label={t('ai.connect')} icon="link" loading={busy === 'token'} disabled={!!busy} onPress={connect} />
            </>
          )}
        </Section>
      ) : null}

      {online ? (
        <Section title={t('ai.inboxTitle')} icon="inbox">
          {inbox && !inbox.length ? <Caption>{t('ai.inboxEmpty')}</Caption> : null}
          {(inbox ?? []).map(item => (
            <View key={item.id} style={{ gap: spacing.xs, paddingVertical: spacing.s }}>
              <Caption>{`${kindLabel(item)} · ${item.source ?? t('ai.removedAssistant')} · ${new Date(item.createdAt).toLocaleString()}`}</Caption>
              <Body>{summary(item)}</Body>
              {item.note ? <Caption>{item.note}</Caption> : null}
              <Row>
                <Button label={t('ai.accept')} icon="check" loading={busy === `accept:${item.id}`} disabled={!!busy} onPress={() => accept(item)} />
                <Button label={t('ai.dismiss')} icon="close" variant="secondary" disabled={!!busy} onPress={() => dismiss(item)} />
              </Row>
            </View>
          ))}
        </Section>
      ) : null}
    </Screen>
  );
}
